import React, { useState } from "react";
import styles from "../styles/SignInHelpForm.module.css";
import { sendSignInHelpForm } from "../helpers/apiHelper";
import { useTranslation } from "../context/TranslationContext";

// Simple email validator (intentionally forgiving)
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ISSUE_KEYS = ["FORGOT_MY_PASSWORD", "PASSWORD_NOT_WORKING", "UNABLE_TO_CHANGE_MY_PASSWORD", "OTHER"];

const SignInHelpForm = ({ initialEmail = "", onClose }) => {
  const [email, setEmail] = useState(initialEmail);
  const [issueType, setIssueType] = useState("");
  const [detail, setDetail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const { t, language } = useTranslation();

  const emailIsValid = isValidEmail(email);
  const issueSelected = Boolean(issueType);
  const detailsProvided = detail.trim().length > 0;

  const isFormValid = emailIsValid && issueSelected && detailsProvided && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!emailIsValid || !issueSelected || !detailsProvided) {
      setError("signInHelpForm.errorRequired");
      return;
    }

    setLoading(true);

    try {
      const response = await sendSignInHelpForm({
        email,
        issueType,
        detail,
        submittedAt: new Date().toISOString(),
        language,
      });

      if (response.success) {
        setSubmitted(true);
      } else {
        setError("signInHelpForm.errorGeneric");
      }
    } catch (err) {
      setError("signInHelpForm.errorSubmit");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.confirmation}>
        <h3>{t("signInHelpForm.sentTitle")}</h3>
        <p>{t("signInHelpForm.sentCopy")}</p>

        <button type="button" className={styles.submitButton} onClick={onClose}>
          {t("signInHelpForm.closeButton")}
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h3 className={styles.heading}>{t("signInHelpForm.title")}</h3>
      <p className={styles.subTitle}>{t("signInHelpForm.subTitle")}</p>

      {error && <p className={styles.error}>{t(error)}</p>}

      {/* Email */}
      <label className={styles.label}>
        {t("signInHelpForm.email")}
        <input
          type="email"
          className={`${styles.input} ${email && !emailIsValid ? styles.invalid : ""}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      {/* Issue type */}
      <label className={styles.label}>
        {t("signInHelpForm.issueType")}
        <select className={styles.select} value={issueType} onChange={(e) => setIssueType(e.target.value)}>
          <option value="" disabled hidden>
            {t("signInHelpForm.issuePlaceholder")}
          </option>

          {ISSUE_KEYS.map((key) => (
            <option key={key} value={key}>
              {t(`signInHelpForm.issues.${key}`)}
            </option>
          ))}
        </select>
      </label>

      {/* Details */}
      <label className={styles.label}>
        {t("signInHelpForm.details")}
        <textarea
          className={styles.textarea}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={4}
          placeholder={t("signInHelpForm.placeholder")}
        />
      </label>

      <button type="submit" className={styles.submitButton} disabled={!isFormValid}>
        {loading ? t("signInHelpForm.sending") : t("signInHelpForm.button")}
      </button>
    </form>
  );
};

export default SignInHelpForm;
